// Boards: HomeLightB (host tab), HomeLightBJoin (join tab), HomeAccountMenu, HomeLoading,
// HomeLightBNoDecks, HomeLightBTooFewCards, DeckPickerSheet, DeckPickerSheetNew.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { t } from '../../i18n';
import { Bdi } from '../../i18n/direction';
import DeckPicker from './DeckPicker';
import { TYPES, answerCardTotal, savePick } from './decks';
import { joinGame } from './join';
import { LanguageList } from './LanguageMenu';
import {
  CodeField, GeneralError, GlobeIcon, Logo, MainButton, NicknameField, PersonIcon, SignOutIcon, Spinner, Wordmark, isBlank, onTap,
} from './parts';

const goneText = types => (types.length === 2
  ? t('home.goneBoth')
  : types[0] === 'question' ? t('home.goneQuestion') : t('home.goneAnswer'));
const NO_PICK = { question: [], answer: [] };

// Up to three deck names, each its own isolated item, then "and N more".
const TileNames = ({ names }) => (
  <>
    {names.slice(0, 3).map((name, i) => <React.Fragment key={i}>{i > 0 && t('home.tileSeparator')}<Bdi>{name}</Bdi></React.Fragment>)}
    {names.length > 3 && t('home.tileMore', { count: names.length - 3 })}
  </>
);
const tileCount = n => t('home.tileCount', { count: n });

const tileStyle = { textDecoration: 'none', minHeight: 120, justifyContent: 'space-between', padding: 14, boxShadow: '0 3px 0 #24102a' };
const pillStyle = { border: '1.5px solid #24102a', borderRadius: 999, padding: '2px 8px', color: '#24102a' };

function DeckTile({ type, names, onOpen }) {
  const title = type === 'question' ? t('home.questionCards') : t('home.answerCards');
  if (names.length === 0) {
    return (
      <a className="wc" href="#decks" onClick={onTap(onOpen)} style={{ textDecoration: 'none', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 14, border: '2px dashed #24102a', borderRadius: 16, background: '#efe4c8', boxShadow: 'none' }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ font: '400 22px/1.15 var(--display)' }}>{title}</span>
          <span style={{ font: '500 13px/1.4 var(--sans)', color: '#6f5f73' }}>{t('home.noDeck')}</span>
        </span>
        <span style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', font: '500 12px var(--sans)' }}><span style={{ ...pillStyle, background: '#ffffff' }}>{t('home.pick')}</span></span>
      </a>
    );
  }
  if (type === 'question') {
    return (
      <a className="bc" href="#decks" onClick={onTap(onOpen)} style={tileStyle}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ font: '400 22px/1.15 var(--display)' }}>{title}</span>
          <span style={{ font: '500 13px/1.4 var(--sans)', color: '#e9d9ec' }}><TileNames names={names} /></span>
        </span>
        <span className="bc-foot"><span>{tileCount(names.length)}</span><span className="pick">{t('home.change')}</span></span>
      </a>
    );
  }
  return (
    <a className="wc" href="#decks" onClick={onTap(onOpen)} style={{ ...tileStyle, display: 'flex', flexDirection: 'column', borderRadius: 16 }}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ font: '400 22px/1.15 var(--display)' }}>{title}</span>
        <span style={{ font: '500 13px/1.4 var(--sans)', color: '#5e4a63' }}><TileNames names={names} /></span>
      </span>
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', font: '500 12px var(--sans)', color: '#5e4a63' }}><span>{tileCount(names.length)}</span><span style={pillStyle}>{t('home.change')}</span></span>
    </a>
  );
}

export default function SignedInHome({
  account, tab, onTab, nickname, onNickname, code, onCode, join, onEnterGame, onAuthFailure, session,
  decks, minAnswerCards, pick, decksFailed, onLoadDecks, onToggleDeck, onOpenDecks, onOpenAccount, onSignOut,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false); // the language list, opened from the account menu
  const accountIcon = useRef(null);
  const [pickerTab, setPickerTab] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createFailed, setCreateFailed] = useState(false);
  const busy = useRef(false);
  const keptCode = useRef(null); // a created game whose join failed; a retry only joins it

  // Each visit reloads the list, so decks built or deleted meanwhile show up.
  useEffect(() => {
    onLoadDecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (decks === null && !decksFailed) return null; // the first deck load of the session

  const selected = pick?.selected ?? NO_PICK;
  const nameOf = id => decks?.find(d => d.id === id)?.name ?? '';
  const answerCards = answerCardTotal(decks ?? [], selected.answer);
  // One line at most: the "gone" line (only types still empty) wins over the count line; a failed load shows neither.
  const gone = decksFailed || !pick ? [] : TYPES.filter(type => pick.dropped[type] && selected[type].length === 0);
  const tooFew = !decksFailed && decks !== null && answerCards < minAnswerCards && gone.length === 0;
  const describedBy = gone.length > 0 ? 'deck-err' : tooFew ? 'cards-err' : undefined;
  const canCreate = !isBlank(nickname) && decks !== null && selected.question.length > 0 && selected.answer.length > 0
    && answerCards >= minAnswerCards;

  const create = async () => {
    if (busy.current) return;
    busy.current = true;
    setCreating(true);
    setCreateFailed(false);
    const mySession = session.current;
    const stale = () => session.current !== mySession;
    try {
      if (!keptCode.current) {
        // The body is the raw code; skip axios' JSON parsing so a code like "1E05" stays a string.
        const res = await axios.post('game', { question_decks: selected.question, answer_decks: selected.answer },
          { transformResponse: [data => data] });
        if (stale()) return;
        savePick(account.uid, selected);
        keptCode.current = res.data;
      }
      const gameCode = keptCode.current;
      let res;
      try {
        res = await joinGame(gameCode, nickname);
      } catch (e) {
        if (e.response?.status === 404) keptCode.current = null; // the kept game is gone; create anew next time
        throw e;
      }
      if (stale()) return;
      keptCode.current = null;
      onEnterGame(gameCode, res.data.player, res.data.player.name);
    } catch (e) {
      if (stale() || await onAuthFailure(e)) return;
      setCreateFailed(true);
    } finally {
      busy.current = false;
      if (!stale()) setCreating(false);
    }
  };

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-start' }}>
        <div style={{ font: '400 28px/1 var(--display)' }}><Wordmark accent="#ee5a4a" /></div>
        <Logo width="48" height="48" />
      </div>
      <a className="btn btn-sm" href="#decks" onClick={onTap(() => onOpenDecks('answer'))}>{t('home.myDecks')}</a>
      <a ref={accountIcon} className="icon-btn" href="#menu" onClick={onTap(() => setMenuOpen(o => !o))} aria-label={t('home.accountMenu')}
        aria-haspopup="menu" aria-expanded={menuOpen ? 'true' : 'false'}
        style={{ border: '2px solid #24102a', borderRadius: 999, ...(menuOpen ? { background: '#7fe0b5' } : {}) }}><PersonIcon size="22" /></a>
    </div>
  );

  const tabs = (
    <div className="seg" role="tablist" aria-label={t('home.whatNow')} style={{ marginTop: 8 }}>
      {tab === 'host'
        ? <button type="button" className="seg-btn seg-on" role="tab" aria-selected="true">{t('home.host')}</button>
        : <a className="seg-btn" role="tab" aria-selected="false" href="#host" onClick={onTap(() => onTab('host'))} style={{ textDecoration: 'none' }}>{t('home.host')}</a>}
      {tab === 'join'
        ? <button type="button" className="seg-btn seg-on" role="tab" aria-selected="true">{t('home.joinWithCode')}</button>
        : <a className="seg-btn" role="tab" aria-selected="false" href="#join" onClick={onTap(() => onTab('join'))} style={{ textDecoration: 'none' }}>{t('home.joinWithCode')}</a>}
    </div>
  );

  const hostBody = (
    <>
      <NicknameField value={nickname} onChange={onNickname} locked={creating} withHint />
      <div className="field" style={{ gap: 10 }}>
        <span className="label">{t('home.decksForGame')}</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, ...(creating ? { opacity: 0.6, pointerEvents: 'none' } : {}) }}
          aria-disabled={creating ? 'true' : undefined}>
          <DeckTile type="question" names={selected.question.map(nameOf)} onOpen={() => setPickerTab('question')} />
          <DeckTile type="answer" names={selected.answer.map(nameOf)} onOpen={() => setPickerTab('answer')} />
        </div>
        {gone.length > 0 && <p className="err" id="deck-err" role="status" style={{ margin: 0 }}>{goneText(gone)}</p>}
      </div>
      {tooFew && (
        <p className="err" id="cards-err" role="status" style={{ margin: 0 }}>
          {t('home.tooFew', { count: answerCards, min: minAnswerCards })}
        </p>
      )}
      <div style={{ flex: 1 }}></div>
      {creating
        ? <button type="button" className="btn btn-primary btn-block" aria-busy="true" aria-label={t('home.creating')}><Spinner /></button>
        : <MainButton enabledClass="btn-primary" disabled={!canCreate} onClick={create} aria-describedby={describedBy}>{t('home.create')}</MainButton>}
      {(decksFailed || createFailed) && <GeneralError />}
    </>
  );

  const joinBody = (
    <>
      <CodeField value={code} onChange={onCode} notFound={join.error === 'notFound'} blocked={join.blocked} placeholder={t('home.codePlaceholder')} />
      <NicknameField value={nickname} onChange={onNickname} taken={join.error === 'nameTaken'} withHint />
      <div style={{ flex: 1 }}></div>
      <MainButton enabledClass="btn-primary" disabled={isBlank(nickname) || join.blocked} onClick={join.run}>{t('home.join')}</MainButton>
      {join.error === 'general' && <GeneralError />}
    </>
  );

  return (
    <main className="screen">
      <div className="body" style={{ gap: 16, paddingTop: 12, paddingBottom: 28 }} aria-hidden={pickerTab ? 'true' : undefined}>
        {header}
        {tabs}
        {tab === 'host' ? hostBody : joinBody}
      </div>
      {menuOpen && (
        <div className="menu" role="menu" aria-label={t('home.accountMenu')}>
          <p className="menu-head">{t('home.signedInAs')}<b><Bdi>{account.email}</Bdi></b></p>
          <a className="menu-item" role="menuitem" href="#account" onClick={onTap(onOpenAccount)}><PersonIcon size="20" />{t('home.myAccount')}</a>
          <a className="menu-item" role="menuitem" aria-haspopup="menu" href="#language"
            onClick={onTap(() => { setMenuOpen(false); setLanguageOpen(true); })}>
            <GlobeIcon size="20" />{t('language.label')}
            <span style={{ marginInlineStart: 'auto', font: '500 14px var(--sans)', color: '#5e4a63' }}>{t('language.name')}</span>
          </a>
          <a className="menu-item" role="menuitem" href="#signout" onClick={onTap(onSignOut)}><SignOutIcon />{t('home.signOut')}</a>
        </div>
      )}
      {languageOpen && <LanguageList onClose={() => { setLanguageOpen(false); accountIcon.current.focus(); }} />}
      {pickerTab && (
        <DeckPicker tab={pickerTab} onTab={setPickerTab} decks={decks ?? []} selected={selected}
          onToggle={onToggleDeck} onDone={() => setPickerTab(null)} onCreateDeck={onOpenDecks} />
      )}
    </main>
  );
}
